import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import './PaymentResult.css';
import tickCircleIcon from '../../assets/images/tickCircleIcon.svg';
import logoImage from '../../assets/images/newlogo.png';
import { commonService } from '../../Network/services/commonService';

const PaymentResult = ({ dispatch }) => {
  const history = useHistory();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState('processing');
  const [paymentData, setPaymentData] = useState({});

  // Function to determine plan type based on is_premium field
  const getPlanType = (planData) => {
    if (!planData) return 'premium-plan';
    
    // Use is_premium field from API response
    if (planData.is_premium === "0" || planData.is_premium === 0) {
      return 'lite-plan';
    } else if (planData.is_premium === "1" || planData.is_premium === 1) {
      return 'premium-plan';
    }
    return 'premium-plan'; // default to premium
  };

  useEffect(() => {
    let data;
    
    const urlParams = new URLSearchParams(location.search);
    const hasUrlParams = urlParams.get('order_id') || urlParams.get('status') || urlParams.get('amount');
    
    if (hasUrlParams) {
      data = {
        orderId: urlParams.get('order_id') || '',
        orderToken: urlParams.get('order_token') || '',
        status: urlParams.get('status') || '',
        paymentId: urlParams.get('payment_id') || '',
        amount: urlParams.get('amount') || '₹0.00'
      };
      
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    } else {
      data = {
        orderId: '',
        orderToken: '',
        status: 'failed',
        paymentId: '',
        amount: '₹0.00'
      };
    }
    setPaymentData(data);
    verifyPayment(data);
  }, []);

  const verifyPayment = async (data) => {
    try {
      setIsLoading(true);
      
      if (!data.orderId) {
        setPaymentStatus('failed');
        return;
      }

      const formData = new FormData();
      formData.append('order_id', data.orderId);
      
      const response = await commonService.CommonService("order_details", formData);
      
      let orderData = response?.data;
      if (orderData?.data) {
        orderData = orderData.data;
      }
      
      const isStatus200 = response?.status === 200 || 
                         response?.status === "200" || 
                         orderData?.status === "200" || 
                         orderData?.status === 200;
      
      if (isStatus200 && orderData) {
        const orderStatus = orderData.order_status;
        const userPlan = orderData.user_plans?.[0];
        const paymentStatus = userPlan?.payment_status;
        
        if (orderStatus === "PAID" || paymentStatus === "completed") {
          setPaymentStatus('success');
          setPaymentData(prev => ({
            ...prev,
            orderDetails: orderData, 
            amount: `₹${orderData.order_amount}`,
            orderNote: orderData.order_note
          }));
        } 
        else if (orderStatus === "ACTIVE" && paymentStatus === "failed") {
          setPaymentStatus('failed');
          setPaymentData(prev => ({
            ...prev,
            orderDetails: orderData,
            amount: `₹${orderData.order_amount}`,
            orderNote: orderData.order_note
          }));
        } 
        // Default to failed for any other case
        else {
          setPaymentStatus('failed');
        }
      } else {
        setPaymentStatus('failed');
      }
    } catch (error) {
      setPaymentStatus('failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    history.push('/plans');
  };

  const handleUsePlan = () => {
    const userPlan = paymentData.orderDetails?.user_plans?.[0];
    const subscriptionDetail = paymentData.orderDetails?.subscription_details?.[0];
    
    if (userPlan && subscriptionDetail) {
      
      const plan = {
        id: userPlan.user_plan_id, 
        type: subscriptionDetail.subscription_name,
        status: userPlan.job_id ? 'used' : 'unused',
        purchaseDate: userPlan.start_date ? 
          new Date(userPlan.start_date).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          }) : (paymentData.orderDetails?.created_at ? 
            new Date(paymentData.orderDetails.created_at).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            }) : null),
        is_premium: subscriptionDetail.is_premium || "0"
      };
      
      dispatch({ type: 'SELECT_PLAN', payload: plan });
    }
    
    history.push('/postjob');
  };


  if (isLoading) {
    return (
      <div className="payment-processing-container">
        <div className="processing-card">
          <div className="logo-container">
            <div className="fws-logo">
              <img src={logoImage} alt="FWS Logo" className="logo-image" />
            </div>
          </div>
          <h2 className="processing-title">Processing Your Payment</h2>
          <p className="processing-description">Please hold on while we securely process your payment.</p>
          <div className="redirect-message">Loading in few Seconds...</div>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <div className="payment-result-container">
        <div className="result-card success-card">
          <div className="status-icon success-icon">
            <img src="/assets/images/PaymentSuccessfulIcon.svg" alt="Payment Successful" className="payment-icon" />
          </div>
          
          <h2 className="main-title">Plan Purchased Successfully 🎉</h2>
          <p className="confirmation-text">A confirmation email has been sent to your registered email address.</p>
          
          <div className={`plan-tag ${getPlanType(paymentData.orderDetails?.subscription_details?.[0])}`}>
            {paymentData.orderDetails?.subscription_details?.[0]?.subscription_name || 'GigFWS Lite'}
          </div>
          <div className="purchase-date">
            Purchased on {paymentData.orderDetails?.created_at ? 
              new Date(paymentData.orderDetails.created_at).toLocaleDateString('en-GB') : 
              new Date().toLocaleDateString('en-GB')
            }
          </div>
          
          <div className="plan-features">
            {paymentData.orderDetails?.subscription_details?.[0]?.content ? (
              paymentData.orderDetails.subscription_details[0].content.map((feature, index) => (
                <div key={index} className="feature-item">
                  <img src={tickCircleIcon} alt="check" className="feature-check" />
                  <span className="feature-text">{feature}</span>
                </div>
              ))
            ) : (
              // Fallback to static features if no content available
              <>
                <div className="feature-item">
                  <img src={tickCircleIcon} alt="check" className="feature-check" />
                  <span className="feature-text">Direct access to all job applicants and contacts with identity verification</span>
                </div>
                <div className="feature-item">
                  <img src={tickCircleIcon} alt="check" className="feature-check" />
                  <span className="feature-text">Access upto 6 months</span>
                </div>
                <div className="feature-item">
                  <img src={tickCircleIcon} alt="check" className="feature-check" />
                  <span className="feature-text">100% money back, if no job applicant found</span>
                </div>
              </>
            )}
          </div>
          
          <div className="button-group">
            <button className="use-plan-btn" onClick={handleUsePlan}>
              Use Plan
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'failed') {
    return (
      <div className="payment-result-container">
        <div className="result-card error-card">
          <div className="status-icon error-icon">
            <img src="/assets/images/PaymentFailedIcon.svg" alt="Payment Failed" className="payment-icon" />
          </div>
          
          <h2 className="main-title">Payment Failed</h2>
          <p className="confirmation-text">There was an error processing your payment. Please try again.</p>
          
          <div className={`plan-tag ${getPlanType(paymentData.orderDetails?.subscription_details?.[0])}`}>
            {paymentData.orderDetails?.subscription_details?.[0]?.subscription_name || 'GigFWS Premium'}
          </div>
          <div className="purchase-date">
            Attempted on {paymentData.orderDetails?.created_at ? 
              new Date(paymentData.orderDetails.created_at).toLocaleDateString('en-GB') : 
              new Date().toLocaleDateString('en-GB')
            }
          </div>
         
          
          <button className="retry-purchase-btn" onClick={handleRetry}>
            Retry Purchase
          </button>
        </div>
      </div>
    );
  }

  return null;
};

const mapStateToProps = (state) => ({
  // Add any state props you need
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(PaymentResult);
